/**
 * Antenatal domain model (JSON:API-shaped).
 *
 * Relationships:
 * - Patient 1—* PregnancyEpisode (usually one active)
 * - PregnancyEpisode 1—* AncVisit
 * - AncVisit 0..1 Encounter (outpatient encounter when visit is started in EMR)
 * - AncFormTemplate drives visit field layout per facility
 *
 * Maternity continuum phases attach to the same PregnancyEpisode:
 * active → in_labour → delivered/postnatal → completed
 * Early exits: transferred | loss | archived
 */

export type PregnancyRiskLevel = 'low' | 'moderate' | 'high'
export type PregnancyStatus =
  | 'active'
  | 'in_labour'
  | 'delivered'
  | 'postnatal'
  | 'completed'
  | 'transferred'
  | 'loss'
  | 'archived'
export type AncVisitStatus = 'scheduled' | 'in_progress' | 'completed' | 'missed' | 'cancelled'
export type PostnatalVisitStatus = AncVisitStatus

/** Early-exit outcomes (non-delivery). Delivered path uses Start labour. */
export type EndPregnancyOutcome = 'transferred' | 'loss' | 'lost_to_follow_up'

export type DeliveryMode = 'svd' | 'assisted' | 'cs'
export type DeliveryOutcome = 'live_birth' | 'stillbirth'

export type ApgarComponentKey = 'activity' | 'pulse' | 'grimace' | 'appearance' | 'respiration'

/** Each component is 0, 1, or 2. The minute total is their sum (0–10). */
export type ApgarComponents = Record<ApgarComponentKey, number>

export type ObEncounterType = 'prenatal' | 'labour' | 'postpartum'

export type ObOrderItemKind = 'lab' | 'imaging' | 'procedure' | 'medicine'

export interface ObOrderSetItem {
  kind: ObOrderItemKind
  name: string
  catalog_id?: number | string
  charge_amount?: number
  dose?: string
  dose_unit?: string
  form?: string
  strength?: string
  frequency?: string
  duration?: string
}

export interface ObOrderSet {
  id: string
  type: 'ob_order_set'
  attributes: {
    name: string
    encounter_type: ObEncounterType
    items: ObOrderSetItem[]
    created_at: string
    updated_at: string
  }
}

export const PREGNANCY_CONTINUUM_STATUSES: PregnancyStatus[] = ['active', 'in_labour', 'delivered', 'postnatal']

export const PREGNANCY_CLOSED_STATUSES: PregnancyStatus[] = ['completed', 'transferred', 'loss', 'archived']
export type AncSchedulePreset = 'focused_4' | 'who_8' | 'custom'

export type AncFormFieldType = 'text' | 'number' | 'date' | 'select' | 'boolean' | 'textarea'

export interface AncFormField {
  key: string
  label: string
  type: AncFormFieldType
  required?: boolean
  unit?: string
  options?: string[]
  section: string
}

export interface AncFormTemplate {
  id: string
  type: 'anc_form_template'
  attributes: {
    name: string
    facility_id?: string
    schedule_preset: AncSchedulePreset
    is_default: boolean
    fields: AncFormField[]
    created_at: string
    updated_at: string
  }
}

export interface PregnancyEpisodeAttributes {
  patient_id: string
  patient_name: string
  mrn?: string
  /** Facility ANC registration / card number (auto-suggested, editable) */
  anc_number: string
  lmp: string
  edd: string
  gravida: number
  parity: number
  booking_date: string
  risk_level: PregnancyRiskLevel
  risk_flags: string[]
  status: PregnancyStatus
  schedule_preset: AncSchedulePreset
  notes?: string
  labour_started_at?: string | null
  delivery_at?: string | null
  postnatal_started_at?: string | null
  /** Encounter opened for labor and delivery notes and orders, when the API stores it. */
  labour_encounter_id?: string | null
  created_at: string
  updated_at: string
}

export interface DeliveryRecordAttributes {
  pregnancy_id: string
  patient_id: string
  delivered_at: string
  place?: string | null
  mode: DeliveryMode
  outcome: DeliveryOutcome
  sex?: string | null
  birth_weight_kg?: number | null
  apgar_1?: number | null
  apgar_5?: number | null
  apgar_1_components?: ApgarComponents | null
  apgar_5_components?: ApgarComponents | null
  complications?: string | null
  notes?: string | null
  created_at: string
  updated_at: string
}

export interface DeliveryRecord {
  id: string
  type: 'delivery_record'
  attributes: DeliveryRecordAttributes
}

/** Frontend PN form schema — mirrors ANC templates; API-backed templates can replace DEFAULT later */
export interface PnFormTemplate {
  id: string
  type: 'pn_form_template'
  attributes: {
    name: string
    mother_fields: AncFormField[]
    newborn_fields: AncFormField[]
    is_default?: boolean
  }
}

export interface PostnatalVisitAttributes {
  pregnancy_id: string
  patient_id: string
  patient_name?: string
  anc_number?: string
  encounter_id?: string | null
  visit_number: number
  contact_label: string
  scheduled_date: string
  completed_date?: string | null
  status: PostnatalVisitStatus
  mother_findings: Record<string, unknown>
  newborn_findings: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface PostnatalVisit {
  id: string
  type: 'postnatal_visit'
  attributes: PostnatalVisitAttributes
}

export interface RecordDeliveryInput {
  delivered_at?: string
  place?: string
  mode: DeliveryMode
  outcome: DeliveryOutcome
  sex?: string
  birth_weight_kg?: number
  apgar_1?: number
  apgar_5?: number
  apgar_1_components?: ApgarComponents
  apgar_5_components?: ApgarComponents
  complications?: string
  notes?: string
}

export interface UpsertPostnatalVisitInput {
  status?: PostnatalVisitStatus
  completed_date?: string
  mother_findings?: Record<string, unknown>
  newborn_findings?: Record<string, unknown>
  encounter_id?: string | null
}

export interface PregnancyEpisode {
  id: string
  type: 'pregnancy_episode'
  attributes: PregnancyEpisodeAttributes
  relationships: {
    patient: { data: { id: string; type: 'patient' } }
    visits: { data: { id: string; type: 'anc_visit' }[] }
    form_template: { data: { id: string; type: 'anc_form_template' } | null }
  }
  delivery_record?: DeliveryRecord
  postnatal_visits?: PostnatalVisit[]
}

export interface AncVisitFindings {
  blood_pressure?: string
  weight_kg?: number
  urine_protein?: string
  urine_glucose?: string
  fundal_height_cm?: number
  fetal_heart_rate?: number
  fetal_lie?: string
  presentation?: string
  tt_given?: boolean
  ipt_given?: boolean
  iron_folate_given?: boolean
  hiv_status?: string
  syphilis_status?: string
  counseling_notes?: string
  next_appointment?: string
  [key: string]: string | number | boolean | undefined
}

export interface AncVisitAttributes {
  pregnancy_id: string
  patient_id: string
  encounter_id?: string | null
  visit_number: number
  contact_label: string
  scheduled_date: string
  completed_date?: string | null
  gestational_age_weeks?: number
  gestational_age_days?: number
  status: AncVisitStatus
  findings: AncVisitFindings
  created_at: string
  updated_at: string
}

export interface AncVisit {
  id: string
  type: 'anc_visit'
  attributes: AncVisitAttributes
  relationships: {
    pregnancy: { data: { id: string; type: 'pregnancy_episode' } }
    patient: { data: { id: string; type: 'patient' } }
    encounter: { data: { id: string; type: 'encounter' } | null }
  }
}

export type MockOrderKind = 'lab' | 'imaging' | 'procedure'
/** Aligned with real lab/imaging order workflow statuses */
export type MockOrderStatus = string
/** Pharmacy / medication workflow status from API meta.status */
export type MockRxStatus = string

/** Mock outpatient encounter created when an ANC visit is started (no intake) */
export interface MockAncEncounter {
  id: string
  type: 'mock_anc_encounter'
  attributes: {
    visit_id: string
    pregnancy_id: string
    patient_id: string
    encounter_type: 'outpatient'
    purpose: string
    status: 'in_progress' | 'completed'
    started_at: string
    completed_at?: string | null
  }
}

export interface MockAncOrder {
  id: string
  type: 'mock_anc_order'
  attributes: {
    visit_id: string
    encounter_id: string
    kind: MockOrderKind
    /** Joined display label (like lab_order test_names join) */
    name: string
    test_names: string[]
    status: MockOrderStatus
    result_summary?: string
    ordered_at: string
    resulted_at?: string | null
    charge_amount: number
  }
}

export interface MockAncPrescription {
  id: string
  type: 'mock_anc_prescription'
  attributes: {
    visit_id: string
    encounter_id: string
    drug_name: string
    dose: string
    frequency: string
    duration: string
    status: MockRxStatus
    prescribed_at: string
    charge_amount: number
  }
}

export interface MockAncInvoiceLine {
  id: string
  type: 'mock_anc_invoice_line'
  attributes: {
    visit_id: string
    encounter_id: string
    description: string
    category: 'consultation' | 'laboratory' | 'imaging' | 'procedure' | 'medication'
    source: 'consultation' | 'order' | 'prescription'
    source_id?: string
    unit_price: number
    quantity: number
    amount: number
  }
}

export interface AddMockOrderItem {
  name: string
  charge_amount?: number
  /** Catalog id when selected from /lab_tests or /imagings */
  catalog_id?: number | string
}

export interface AddMockOrderInput {
  visit_id: string
  kind: MockOrderKind
  /** Multi-select: one or more tests/studies */
  items: AddMockOrderItem[]
  /** When ordering from labour or postpartum, the visit encounter is not the ANC visit. */
  encounter_id?: string
  patient_id?: string
}

/** Row shape for LabTech / Radiologist ANC mock merge */
export interface AncWorklistOrderRow {
  id: string
  kind: 'lab' | 'imaging'
  patient_id: string
  patient_name: string
  test_label: string
  ordered_by: string
  ordered_at: string
  status: MockOrderStatus
  visit_id: string
  is_anc_mock: true
}

export interface AddMockPrescriptionInput {
  visit_id: string
  encounter_id?: string
  patient_id?: string
  drug_name: string
  dose: string
  dose_unit?: string
  form?: string
  strength: string
  frequency: string
  frequency_id?: string
  duration: string
  start_at?: string
  charge_amount?: number
}

export interface AncRegisterRow {
  pregnancy_id: string
  visit_id?: string
  patient_id: string
  patient_name: string
  mrn?: string
  anc_number?: string
  gestational_age_label: string
  risk_level: PregnancyRiskLevel
  next_contact: string
  visit_status?: AncVisitStatus
  scheduled_date: string
  schedule_preset?: AncSchedulePreset
  order_count?: number
  rx_count?: number
}

export interface BookPregnancyInput {
  patient_id: string
  patient_name: string
  mrn?: string
  anc_number?: string
  lmp: string
  gravida: number
  parity: number
  risk_level?: PregnancyRiskLevel
  risk_flags?: string[]
  notes?: string
  schedule_preset?: AncSchedulePreset
  form_template_id?: string
}

export interface UpsertAncVisitInput {
  pregnancy_id?: string
  findings?: AncVisitFindings
  status?: AncVisitStatus
  completed_date?: string
  encounter_id?: string | null
}
